import React, { useState } from 'react';

import {
  Box,
  Flex,
  Heading,
  Text,
  useMediaQuery,
  Image,
  Container,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { WebPImage } from '@/components/WebPImage';

import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';

//takes texts from locale json file by key
const team = [
  {
    name: 'Barry Laden',
    position: 'main.managementTeam.firstMember',
    logoCommon: 'images/Barry.png',
    logoWebp: 'images/webp/Barry.webp',
    profile: 'https://www.linkedin.com/in/barry-laden-mbe-3128697/',
  },
  {
    name: 'Maj Rahman',
    position: 'main.managementTeam.secondMember',
    logoCommon: 'images/Maj.png',
    logoWebp: 'images/webp/Maj.webp',
    profile: 'https://www.linkedin.com/in/maj-rahman-222695248/',
  },
  {
    name: 'Justine Herrera',
    position: 'main.managementTeam.thirdMember',
    logoCommon: 'images/Justine.png',
    logoWebp: 'images/webp/Justine.webp',
    profile: 'https://www.linkedin.com/in/dr-justine-scerri-herrera-6b377278/',
  },
  {
    name: 'Paul Armstrong',
    position: 'main.managementTeam.fourthMember',
    logoCommon: 'images/Paul.png',
    logoWebp: 'images/webp/Paul.webp',
    profile: 'https://www.linkedin.com/in/paulvarmstrong/',
  },
];

/**
 * Component for Management section in Home page
 * @component
 * @example
 * <ManagementSection />.
 */
const ManagementSection: React.FC = () => {
  const [profile, setProfile] = useState(0);
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery(MediaQueryAmountsEnum.isMobile);
  const nextProfile = () => {
    if (profile < 3) {
      setProfile(profile + 1);
    }
  };
  const prevProfile = () => {
    if (profile > 0) {
      setProfile(profile - 1);
    }
  };

  return (
    <Flex
      id='management-team'
      sx={{
        p: { base: '60px 40px 60px', lg: '140px 20px', xl: '140px 0' },
        color: 'app.black',
        alignItems: 'center',
        flexDirection: 'column',
        background: 'app.lightWhite',
      }}
    >
      <Box>
        <Heading as='h1' color='app.pink' size='3xl' textAlign='center'>
          {t('main.managementTeam.title')}
        </Heading>

        <Box
          background='app.pink'
          h='4px'
          m='20px auto 25px'
          w={{ base: '50px', md: '66px' }}
        />
      </Box>

      <Box
        display={{ base: 'block', lg: 'none' }}
        fontSize='xl'
        key={team[profile].name}
        m={{ base: 'auto', lg: '0' }}
        maxW='100%'
        minW='225px'
        w='100%'
      >
        <Flex alignItems='center' gap='18px' justifyContent='center'>
          <Flex
            alignItems='center'
            backgroundColor={profile === 0 ? 'app.lightPink' : 'app.pink'}
            borderRadius='30px'
            h='29px'
            justifyContent='center'
            onClick={() => prevProfile()}
            w='29px'
          >
            <Image alt='arrow' src='icons/arrow1.svg' />
          </Flex>
          <WebPImage
            alt={team[profile].name}
            commonPath={team[profile].logoCommon}
            sx={{
              minWidth: { base: '185px', lg: '164px' },
              height: { base: '185px', lg: '164px' },
              margin: '20px auto 20px',
              backgroundColor: 'app.white',
              boxShadow: '4px 3px 20px -1px rgba(0, 0, 0, 0.12)',
              borderRadius: '50%',
            }}
            webpPath={team[profile].logoWebp}
          />
          <Flex
            alignItems='center'
            backgroundColor={profile === 3 ? 'app.lightPink' : 'app.pink'}
            borderRadius='30px'
            h='29px'
            justifyContent='center'
            onClick={() => nextProfile()}
            w='29px'
          >
            <Image alt='arrow' src='icons/arrow2.svg' />
          </Flex>
        </Flex>

        <Flex fontWeight='bold' justify='center' pb='7px'>
          <Text fontSize='18px' textAlign='center'>
            {team[profile].name}
          </Text>
          <a
            href={team[profile].profile}
            rel='noopener noreferrer'
            target='_blank'
          >
            <WebPImage
              alt={team[profile].name + '(linkedIn)'}
              commonPath='/icons/linkedin.png'
              sx={{
                width: '18px',
                height: '18px',
                ml: '16px',
                mt: '4px',
              }}
              webpPath='/icons/webp/linkedin.webp'
            />
          </a>
        </Flex>
        <Box
          backgroundColor='#FBC0DE'
          h='3px'
          m='auto'
          w={profile === 0 ? '152px' : profile === 2 ? '162px' : '180px'}
        />

        <Text fontSize='16px' mb='sm' mt='7px' textAlign='center'>
          {t(team[profile].position)}
        </Text>
      </Box>

      <Container
        display={{ base: 'none', lg: 'flex' }}
        justifyContent='space-between'
        maxW='1440px'
        sx={{
          overflow: 'auto',
          whiteSpace: 'nowrap',
          pb: { base: '70px', lg: '0' },
          px: '20px',
        }}
        w='100%'
      >
        {!isMobile &&
          team.map((item) => {
            return (
              <Box
                fontSize='xl'
                key={item.name}
                maxW='225px'
                minW='225px'
                mr={{ base: '80px', lg: '0' }}
                w='100%'
              >
                <WebPImage
                  alt={item.name}
                  commonPath={item.logoCommon}
                  sx={{
                    minWidth: { base: '125px', lg: '164px' },
                    height: { base: '125px', lg: '164px' },
                    margin: '20px auto 48px',
                    backgroundColor: 'app.white',
                    boxShadow: '4px 3px 20px -1px rgba(0, 0, 0, 0.12)',
                    borderRadius: '50%',
                  }}
                  webpPath={item.logoWebp}
                />

                <Flex
                  borderBottom='1px solid #FD108A'
                  fontWeight='bold'
                  justify='space-between'
                  mb='sm'
                  pb='sm'
                >
                  <Text>{item.name}</Text>
                  <a
                    href={item.profile}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    <WebPImage
                      alt={item.name + '(linkedIn)'}
                      commonPath='/icons/linkedin.png'
                      sx={{
                        width: '30px',
                        height: '30px',
                      }}
                      webpPath='/icons/webp/linkedin.webp'
                    />
                  </a>
                </Flex>

                <Text mb='sm'>{t(item.position)}</Text>
              </Box>
            );
          })}
      </Container>
    </Flex>
  );
};

export default ManagementSection;
